import React, { useState, useEffect } from 'react';
import { ArrowRight, TrendingUp, Activity, Eye, Target, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Animated counter hook
  const useCountUp = (end, duration = 2000) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      let startTime;
      let animationFrame;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = (currentTime - startTime) / duration;

        if (progress < 1) {
          setCount(Math.floor(end * progress));
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };

      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }, [end, duration]);

    return count;
  };

  // Live stats with animation
  const activeCampaigns = useCountUp(847, 2000);
  const impressionsToday = useCountUp(2840000, 2500);
  const conversionsWeek = useCountUp(18420, 2000);
  const roiGenerated = useCountUp(4250000, 2500);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <Header />
      
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwYW5hbHl0aWNzJTIwZGFzaGJvYXJkfGVufDB8fHxibGFja3wxNzcxNzQ4NzA4fDA&ixlib=rb-4.1.0&q=85"
          alt="Digital Marketing Analytics"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/95 to-slate-950/80"></div>
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 backdrop-blur-sm">
              <TrendingUp className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-400 font-medium">
                Data-Driven Performance Marketing
              </span>
            </div>

            {/* Heading */}
            <div className="space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight">
                Elevate Your
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                  Digital Performance
                </span>
              </h1>
              <p className="text-xl text-slate-400 leading-relaxed max-w-2xl">
                Unique and targeted data-driven performance solutions designed to provide superior transparency, insights, and scalable results across all channels.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={scrollToContact}
                className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-lg rounded-lg group transition-all duration-300 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => {
                  const element = document.getElementById('services');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                variant="outline"
                className="border-slate-700 hover:border-cyan-500 text-white px-8 py-6 text-lg rounded-lg bg-slate-900/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300"
              >
                Our Services
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center gap-8 pt-8 border-t border-slate-800/50">
              <div>
                <p className="text-3xl font-bold text-white">50K+</p>
                <p className="text-sm text-slate-400 mt-1">Campaigns Delivered</p>
              </div>
              <div className="w-px h-12 bg-slate-800"></div>
              <div>
                <p className="text-3xl font-bold text-white">95%</p>
                <p className="text-sm text-slate-400 mt-1">Client Retention</p>
              </div>
            </div>
          </div>

          {/* Right Side - Live Stats Counter */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="glass-card p-8 rounded-2xl bg-slate-900/40 backdrop-blur-xl border border-slate-800/50">
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">Live Performance</h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                      <span className="text-green-400 text-xs font-medium">Real-time</span>
                    </div>
                  </div>

                  {/* Animated Stats */}
                  <div className="space-y-5">
                    {/* Active Campaigns */}
                    <div className="flex items-start space-x-4 p-4 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 transition-colors duration-300">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-white tabular-nums">
                          {activeCampaigns.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">Active Campaigns</p>
                      </div>
                    </div>

                    {/* Impressions Today */}
                    <div className="flex items-start space-x-4 p-4 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 transition-colors duration-300">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                        <Eye className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-white tabular-nums">
                          {(impressionsToday / 1000000).toFixed(2)}M
                        </p>
                        <p className="text-xs text-slate-400 mt-1">Impressions Today</p>
                      </div>
                    </div>

                    {/* Conversions This Week */}
                    <div className="flex items-start space-x-4 p-4 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 transition-colors duration-300">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                        <Target className="w-5 h-5 text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-white tabular-nums">
                          {conversionsWeek.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">Conversions This Week</p>
                      </div>
                    </div>

                    {/* ROI Generated */}
                    <div className="flex items-start space-x-4 p-4 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 transition-colors duration-300">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-white tabular-nums">
                          ${(roiGenerated / 1000000).toFixed(1)}M
                        </p>
                        <p className="text-xs text-slate-400 mt-1">ROI Generated (30 days)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
